import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import UserProfile from '../common/UserProfile';
import EarningsReport from './EarningsReport';
import TripTracker from './TripTracker';

const DriverView = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tripHistoryTab, setTripHistoryTab] = useState('current');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [trips, setTrips] = useState([]);
  const [completedTrips, setCompletedTrips] = useState([]);
  const [showProfile, setShowProfile] = useState(false);
  const [showEarningsReport, setShowEarningsReport] = useState(false);
  const [showTripTracker, setShowTripTracker] = useState(false);
  const [activeTripLocked, setActiveTripLocked] = useState(false); // Lock state for active trips
  const [dashboardPeriod, setDashboardPeriod] = useState('this_month'); // New: dashboard period toggle
  const [loading, setLoading] = useState(true);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    if (isAuthenticated && user) {
      loadDriverData();
    }
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Listen for earnings report event from header
    const handleOpenEarningsReport = () => {
      setShowEarningsReport(true);
    };

    window.addEventListener('openEarningsReport', handleOpenEarningsReport);
    
    return () => {
      window.removeEventListener('openEarningsReport', handleOpenEarningsReport);
    };
  }, []);

  const loadDriverData = async () => {
    try {
      setLoading(true);
      
      // Get driver profile
      const driverResponse = await fetch(`${BACKEND_URL}/api/drivers`);
      if (driverResponse.ok) {
        const drivers = await driverResponse.json();
        const currentDriver = drivers.find(d => d.user_id === user.id) || {
          user_id: user.id,
          name: user.name,
          phone: user.phone,
          email: user.email,
          license_number: 'DL' + Math.random().toString().substr(2, 8),
          vehicle_make: 'Honda',
          vehicle_model: 'Jazz',
          vehicle_year: 2020,
          vehicle_color: 'Blue',
          vehicle_plate: 'GP',
          is_available: true,
          rating: 5.0,
          total_trips: 0,
          today_earnings: 0,
          weekly_earnings: 0
        };
        
        // Get driver earnings data
        try {
          const earningsResponse = await fetch(`${BACKEND_URL}/api/drivers/${user.id}/earnings`);
          if (earningsResponse.ok) {
            const earningsData = await earningsResponse.json();
            currentDriver.monthly_earnings = earningsData.current_month.earnings;
            currentDriver.all_time_earnings = earningsData.summary.total_earnings;
            currentDriver.total_trips_completed = earningsData.current_month.trips;
            currentDriver.all_time_trips = earningsData.summary.total_trips;
            currentDriver.total_earnings = earningsData.summary.total_earnings;
            currentDriver.average_rating = earningsData.summary.average_rating;
            currentDriver.rating = earningsData.summary.average_rating;
          }
        } catch (e) {
          console.log('Earnings data not available yet');
        }
        
        setDriverData(currentDriver);
      }
      
      // Load driver's assigned bookings (current trips)
      const assignedResponse = await fetch(`${BACKEND_URL}/api/driver/assigned-bookings/${user.id}`);
      if (assignedResponse.ok) {
        const assignedData = await assignedResponse.json();
        // Sort by most recent first
        const sortedTrips = assignedData.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        setTrips(sortedTrips);
        
        // Check for active (in_progress) trip and force into trip mode
        const activeTrip = sortedTrips.find(trip => trip.status === 'in_progress');
        if (activeTrip) {
          console.log('🚨 Active trip detected - forcing into trip mode:', activeTrip.id);
          setSelectedTrip(activeTrip);
          setShowTripTracker(true);
          setActiveTripLocked(true);
        }
      }
      
      // Load completed trips (trip history)
      const allBookingsResponse = await fetch(`${BACKEND_URL}/api/bookings`);
      if (allBookingsResponse.ok) {
        const allBookings = await allBookingsResponse.json();
        // Find driver ID first
        const driverInfo = await fetch(`${BACKEND_URL}/api/drivers`);
        if (driverInfo.ok) {
          const drivers = await driverInfo.json();
          const currentDriver = drivers.find(d => d.user_id === user.id);
          if (currentDriver) {
            const completedTrips = allBookings
              .filter(booking => booking.driverId === currentDriver.id && booking.status === 'completed')
              .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            setCompletedTrips(completedTrips);
          }
        }
      }
      
    } catch (error) {
      console.error('Failed to load driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isRoundTrip = (booking) => {
    return booking.quoteDetails?.oneWayDistance && booking.quoteDetails?.totalDistance && 
           (parseFloat(booking.quoteDetails.totalDistance) > parseFloat(booking.quoteDetails.oneWayDistance) * 1.5);
  };

  const toggleStatus = async () => {
    try {
      const newStatus = !driverData.is_available;
      const response = await fetch(`${BACKEND_URL}/api/drivers/${user.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_available: newStatus })
      });
      
      if (response.ok) {
        setDriverData(prev => ({ ...prev, is_available: newStatus }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to accept this booking?')) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/driver/booking/${bookingId}/accept?driver_id=${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        loadDriverData();
        alert('Booking accepted successfully! Trip is now confirmed.');
      } else {
        throw new Error('Failed to accept booking');
      }
    } catch (error) {
      console.error('Failed to accept booking:', error);
      alert('Failed to accept booking. Please try again.');
    }
  };

  const handleDeclineBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to decline this booking? It will be reassigned to another driver.')) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/driver/booking/${bookingId}/decline?driver_id=${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        loadDriverData();
        alert('Booking declined successfully! It has been returned for reassignment.');
      } else {
        throw new Error('Failed to decline booking');
      }
    } catch (error) {
      console.error('Failed to decline booking:', error);
      alert('Failed to decline booking. Please try again.');
    }
  };

  const handleStartTrip = (bookingId) => {
    const booking = trips.find(t => t.id === bookingId);
    if (booking) {
      setSelectedTrip(booking);
      setShowTripTracker(true);
    }
  };

  const handleCompleteTrip = (bookingId) => {
    const booking = trips.find(t => t.id === bookingId);
    if (booking) {
      setSelectedTrip(booking);
      setShowTripTracker(true);
    }
  };

  const handleTripUpdate = () => {
    // Refresh trip data after trip updates
    loadDriverData();
    
    // Check if the trip was completed, if so unlock trip mode
    setTimeout(async () => {
      const assignedResponse = await fetch(`${BACKEND_URL}/api/driver/assigned-bookings/${user.id}`);
      if (assignedResponse.ok) {
        const assignedData = await assignedResponse.json();
        const activeTrip = assignedData.find(trip => trip.status === 'in_progress');
        if (!activeTrip) {
          // No more active trips - unlock trip mode
          setActiveTripLocked(false);
          setShowTripTracker(false);
          console.log('✅ Trip completed - unlocking trip mode');
        }
      }
    }, 1000);
  };

  const renderTripCard = (booking, isHistory = false) => {
    return (
      <div key={booking.id} className="trip-card-clean">
        <div className="trip-main-content">
          <div className="trip-route-section">
            <div className="trip-route-clean">
              <span className="pickup">📍 {booking.pickupAddress.length > 35 ? booking.pickupAddress.substring(0, 35) + '...' : booking.pickupAddress}</span>
              <div className="route-connector">
                {isRoundTrip(booking) ? '⇄' : '→'}
                {isRoundTrip(booking) && <small className="round-trip-label">Round Trip</small>}
              </div>
              <span className="destination">🏁 {booking.destinationAddress.length > 35 ? booking.destinationAddress.substring(0, 35) + '...' : booking.destinationAddress}</span>
            </div>
          </div>

          <div className="trip-details-section">
            <div className="trip-basic-info">
              <div className="info-row">
                <span className="info-item">👤 {booking.passengerName}</span>
                <span className="info-item">📅 {booking.scheduledDate}</span>
                <span className="info-item">⏰ {booking.scheduledTime}</span>
              </div>
              <div className="fare-row">
                <span className="fare-item">📏 {booking.quoteDetails?.totalDistance || booking.quoteDetails?.distance}km</span>
                <span className="fare-item">⏱️ {booking.companionMinutes}min companion</span>
                <span className="fare-amount" data-testid="driver-earnings-display">
                  {isHistory && booking.driver_earnings
                    ? <>
                        💰 R{booking.driver_earnings.toFixed(2)} <small style={{ fontSize: '0.75rem' }}>(your 70%)</small>
                        {booking.actual_fare && booking.pricing_adjustment && booking.pricing_adjustment !== 1 && (
                          <small style={{ display: 'block', color: '#888', fontSize: '0.72rem', textDecoration: 'line-through' }}>
                            est. R{((booking.quoteDetails?.totalCost || booking.quoteDetails?.totalFare || 0) * 0.7).toFixed(2)}
                          </small>
                        )}
                        {booking.fare_adjusted_by_admin && (
                          <small style={{ display: 'block', color: '#A0306A', fontSize: '0.72rem' }}>(fare adjusted)</small>
                        )}
                      </>
                    : `💰 R${booking.quoteDetails?.totalCost || booking.quoteDetails?.totalFare} (est.)`
                  }
                </span>
              </div>
            </div>

            <div className="trip-status-actions">
              <span className={`status-clean status-${booking.status}`}>
                {booking.status.replace('_', ' ').toUpperCase()}
              </span>
              
              {!isHistory && (
                <div className="action-buttons">
                  {booking.status === 'assigned' && (
                    <>
                      <button onClick={() => handleAcceptBooking(booking.id)} className="btn-accept">
                        ✅ Accept
                      </button>
                      <button onClick={() => handleDeclineBooking(booking.id)} className="btn-decline">
                        ❌ Decline
                      </button>
                    </>
                  )}
                  
                  {booking.status === 'confirmed' && (
                    <button onClick={() => handleStartTrip(booking.id)} className="btn-start">
                      🚀 Start
                    </button>
                  )}
                  
                  {booking.status === 'in_progress' && (
                    <button onClick={() => handleCompleteTrip(booking.id)} className="btn-complete">
                      🎯 Continue Trip
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <button 
          className="info-btn"
          onClick={() => setSelectedTrip(booking)}
          title="View full trip details"
        >
          ℹ️
        </button>
      </div>
    );
  };

  const renderTripDetailsModal = () => {
    if (!selectedTrip) return null;

    return (
      <div className="modal-overlay" onClick={() => setSelectedTrip(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Trip Details</h3>
            <button className="modal-close" onClick={() => setSelectedTrip(null)}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="detail-section-modal">
              <h4>🚗 Trip Route</h4>
              <div className="route-details">
                <p><strong>Pickup:</strong> {selectedTrip.pickupAddress}</p>
                <p><strong>Destination:</strong> {selectedTrip.destinationAddress}</p>
                {isRoundTrip(selectedTrip) && <p className="round-trip-notice">🔄 This is a round trip</p>}
              </div>
            </div>

            <div className="detail-section-modal">
              <h4>👤 Passenger Information</h4>
              <div className="passenger-details">
                <p><strong>Name:</strong> {selectedTrip.passengerName}</p>
                <p><strong>Phone:</strong> {selectedTrip.passengerPhone}</p>
                <p><strong>Email:</strong> {selectedTrip.passengerEmail}</p>
              </div>
            </div>

            <div className="detail-section-modal">
              <h4>📅 Schedule & Timing</h4>
              <div className="schedule-details">
                <p><strong>Date:</strong> {selectedTrip.scheduledDate}</p>
                <p><strong>Time:</strong> {selectedTrip.scheduledTime}</p>
                <p><strong>Companion Time:</strong> {selectedTrip.companionMinutes} minutes</p>
              </div>
            </div>

            <div className="detail-section-modal">
              <h4>💰 Fare Breakdown</h4>
              <div className="fare-details">
                <p><strong>Distance:</strong> {selectedTrip.quoteDetails?.totalDistance || selectedTrip.quoteDetails?.distance}km</p>
                <p><strong>Distance Cost:</strong> R{selectedTrip.quoteDetails?.distanceCost || 'N/A'}</p>
                <p><strong>Companion Cost:</strong> R{selectedTrip.quoteDetails?.companionCost || 'N/A'}</p>
                <p><strong>Total Fare:</strong> <span className="total-amount">R{selectedTrip.quoteDetails?.totalCost || selectedTrip.quoteDetails?.totalFare}</span></p>
              </div>
            </div>

            {(selectedTrip.specialRequirements || selectedTrip.wheelchairAccess || selectedTrip.elderlyAssistance || selectedTrip.medicalAppointment || selectedTrip.shoppingAssistance) && (
              <div className="detail-section-modal">
                <h4>📋 Special Requirements</h4>
                <div className="requirements-modal">
                  {selectedTrip.wheelchairAccess && <span className="req-tag">♿ Wheelchair Access</span>}
                  {selectedTrip.elderlyAssistance && <span className="req-tag">👴 Elderly Assistance</span>}
                  {selectedTrip.medicalAppointment && <span className="req-tag">🏥 Medical Appointment</span>}
                  {selectedTrip.shoppingAssistance && <span className="req-tag">🛒 Shopping Assistance</span>}
                  {selectedTrip.specialRequirements && <span className="req-tag">📝 {selectedTrip.specialRequirements}</span>}
                </div>
              </div>
            )}

            {selectedTrip.additionalNotes && (
              <div className="detail-section-modal">
                <h4>📝 Additional Notes</h4>
                <p className="notes-content">{selectedTrip.additionalNotes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="driver-view">
        <div className="driver-welcome">
          <div className="welcome-content">
            <h1>Driver Portal</h1>
            <p>Please sign in to access your driver dashboard</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="driver-view">
        <div className="loading-state">
          <h2>Loading your dashboard...</h2>
        </div>
      </div>
    );
  }

  const renderDashboard = () => {
    // Calculate earnings and trips based on selected period
    const displayEarnings = dashboardPeriod === 'all_time' 
      ? driverData?.all_time_earnings || 0 
      : driverData?.monthly_earnings || 0;
    
    const displayTripsCompleted = dashboardPeriod === 'all_time' 
      ? driverData?.all_time_trips || 0 
      : driverData?.total_trips_completed || 0;
    
    return (
      <div className="driver-dashboard">
        <div className="driver-header">
          <div className="driver-info">
            <h2>Welcome back, {user?.name}! 👋</h2>
            <p>{driverData?.vehicle_make} {driverData?.vehicle_model} • {driverData?.vehicle_plate}</p>
          </div>
          <div className="driver-status">
            <button 
              className={`status-toggle ${driverData?.is_available ? 'online' : 'offline'}`}
              onClick={toggleStatus}
            >
              {driverData?.is_available ? '🟢 Online' : '🔴 Offline'}
            </button>
          </div>
        </div>
        
        {/* Dashboard Period Toggle */}
        <div className="dashboard-period-toggle">
          <h3>📊 Dashboard Overview</h3>
          <div className="period-buttons">
            <button 
              className={`period-btn ${dashboardPeriod === 'this_month' ? 'active' : ''}`}
              onClick={() => setDashboardPeriod('this_month')}
            >
              This Month
            </button>
            <button 
              className={`period-btn ${dashboardPeriod === 'all_time' ? 'active' : ''}`}
              onClick={() => setDashboardPeriod('all_time')}
            >
              All Time
            </button>
          </div>
        </div>
        
        <div className="earnings-summary">
          <div className="earning-card">
            <h3>{dashboardPeriod === 'all_time' ? 'Total Earnings' : 'This Month Earnings'}</h3>
            <p className="amount">R{displayEarnings.toFixed(2)}</p>
          </div>
          <div className="earning-card">
            <h3>Current Trips</h3>
            <p className="amount">{trips.filter(t => ['assigned', 'confirmed', 'in_progress'].includes(t.status)).length}</p>
          </div>
          <div className="earning-card">
            <h3>{dashboardPeriod === 'all_time' ? 'Total Completed' : 'This Month Completed'}</h3>
            <p className="amount">{displayTripsCompleted}</p>
          </div>
          <div className="earning-card">
            <h3>Rating</h3>
            <p className="amount">⭐ {driverData?.rating?.toFixed(1) || '5.0'}</p>
          </div>
        </div>

        {trips.length > 0 && (
          <div className="quick-overview">
            <h3>📋 Quick Overview</h3>
            <div className="overview-stats">
              <div className="stat-item">
                <span className="stat-label">Assigned:</span>
                <span className="stat-value">{trips.filter(t => t.status === 'assigned').length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Confirmed:</span>
                <span className="stat-value">{trips.filter(t => t.status === 'confirmed').length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">In Progress:</span>
                <span className="stat-value">{trips.filter(t => t.status === 'in_progress').length}</span>
              </div>
            </div>
            <p className="overview-note">Click "My Trips" to view and manage all your assigned trips</p>
          </div>
        )}
      </div>
    );
  };

  const renderTrips = () => (
    <div className="driver-trips">
      <div className="trips-header">
        <h2>My Trips</h2>
        <div className="trip-tabs">
          <button 
            className={tripHistoryTab === 'current' ? 'tab-active' : 'tab-inactive'}
            onClick={() => setTripHistoryTab('current')}
          >
            Current Trips ({trips.filter(t => ['assigned', 'confirmed', 'in_progress'].includes(t.status)).length})
          </button>
          <button 
            className={tripHistoryTab === 'history' ? 'tab-active' : 'tab-inactive'}
            onClick={() => setTripHistoryTab('history')}
          >
            Trip History ({completedTrips.length})
          </button>
        </div>
      </div>
      
      {tripHistoryTab === 'current' && (
        <div className="current-trips">
          {trips.length === 0 ? (
            <div className="no-trips-state">
              <h3>No current trips assigned</h3>
              <p>You'll see your assigned trips here when admin assigns them to you.</p>
              <p>Make sure you're online to receive trip assignments!</p>
            </div>
          ) : (
            <div className="trips-list-clean">
              {trips.map(booking => renderTripCard(booking, false))}
            </div>
          )}
        </div>
      )}

      {tripHistoryTab === 'history' && (
        <div className="trip-history">
          {completedTrips.length === 0 ? (
            <div className="no-trips-state">
              <h3>No completed trips yet</h3>
              <p>Your completed trips will appear here.</p>
            </div>
          ) : (
            <div className="trips-list-clean">
              {completedTrips.map(booking => renderTripCard(booking, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="driver-view">
      {/* Show active trip lock overlay */}
      {activeTripLocked && !showTripTracker && (
        <div className="active-trip-overlay">
          <div className="active-trip-banner">
            <h3>🚨 Active Trip in Progress</h3>
            <p>You have an active trip that must be completed before accessing other features.</p>
            <button 
              className="btn-primary" 
              onClick={() => {
                const activeTrip = trips.find(trip => trip.status === 'in_progress');
                if (activeTrip) {
                  setSelectedTrip(activeTrip);
                  setShowTripTracker(true);
                }
              }}
            >
              Return to Trip
            </button>
          </div>
        </div>
      )}
      
      <div className="driver-nav">
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => !activeTripLocked && setActiveTab('dashboard')}
          disabled={activeTripLocked}
        >
          🏠 Dashboard
        </button>
        <button 
          className={activeTab === 'trips' ? 'active' : ''}
          onClick={() => !activeTripLocked && setActiveTab('trips')}
          disabled={activeTripLocked}
        >
          🚗 My Trips ({trips.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'trips' && renderTrips()}
      </div>
      
      {/* Trip Details Modal */}
      {renderTripDetailsModal()}
      
      {/* User Profile Modal */}
      {showProfile && !activeTripLocked && (
        <UserProfile onClose={() => setShowProfile(false)} />
      )}

      {/* Earnings Report Modal */}
      {showEarningsReport && !activeTripLocked && (
        <EarningsReport onClose={() => setShowEarningsReport(false)} />
      )}

      {/* Trip Tracker Modal - Cannot be closed if trip is locked */}
      {showTripTracker && selectedTrip && (
        <TripTracker 
          booking={selectedTrip}
          onClose={activeTripLocked ? null : () => setShowTripTracker(false)}
          onTripUpdate={handleTripUpdate}
          isLocked={activeTripLocked}
        />
      )}
    </div>
  );
};

export default DriverView;